import { useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { useReveal } from "../../hooks/useReveal";
import { Mail, Phone, MapPin, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  nome: "",
  email: "",
  telefono: "",
  messaggio: "",
  consenso_privacy: false,
  website: "", // honeypot anti-bot: non modificare da UI
};

export const Contatti = () => {
  const r = useReveal();
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consenso_privacy) {
      toast.error("È necessario accettare l'informativa privacy.");
      return;
    }
    if (form.messaggio.trim().length < 10) {
      toast.error("Il messaggio deve contenere almeno 10 caratteri.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contacts`, form);
      toast.success("Richiesta inviata. Ti risponderò entro 24 ore lavorative.");
      setForm(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore nell'invio. Riprova tra poco.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contatti"
      data-testid="contatti-section"
      className="bg-[#0A1128] text-ivory py-20 md:py-28 relative"
    >
      <Toaster position="top-center" richColors duration={2500} />
      <div ref={r} className="reveal container-editorial grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <p className="overline text-goldSoft mb-6">Contatti</p>
          <h2 data-testid="contatti-title" className="display-2 text-ivory mb-8">
            Iniziamo da<br />
            <em className="italic text-goldSoft">una conversazione</em>.
          </h2>
          <p className="lede text-ivory/70 max-w-md mb-12">
            Il primo colloquio è riservato e gratuito. Raccontami brevemente la tua
            situazione: ti risponderò personalmente entro 24 ore lavorative.
          </p>

          <ul className="space-y-6 text-ivory/85">
            <li data-testid="contact-info-phone" className="flex items-center gap-4">
              <Phone size={18} className="text-goldSoft" />
              <a href="tel:+393476483171" className="link-underline">
                +39 347 648 3171
              </a>
            </li>
            <li data-testid="contact-info-address" className="flex items-center gap-4">
              <MapPin size={18} className="text-goldSoft" />
              <span>Studio nei pressi di Bergamo · Videocall in tutta Italia</span>
            </li>
          </ul>
        </div>

        <form
          data-testid="contact-form"
          onSubmit={submit}
          className="lg:col-span-7 bg-ivory text-navy p-8 md:p-12 border border-ivory/10"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Nome e cognome" required>
              <input
                data-testid="contact-input-nome"
                value={form.nome}
                onChange={update("nome")}
                required
                minLength={2}
                placeholder="Mario Rossi"
                className="input-editorial"
              />
            </Field>
            <Field label="Email" required>
              <input
                data-testid="contact-input-email"
                type="email"
                value={form.email}
                onChange={update("email")}
                required
                placeholder="mario.rossi@email.it"
                className="input-editorial"
              />
            </Field>
            <Field label="Telefono">
              <input
                data-testid="contact-input-telefono"
                value={form.telefono}
                onChange={update("telefono")}
                placeholder="+39 ..."
                className="input-editorial"
              />
            </Field>
            <Field label="Argomento">
              <input
                data-testid="contact-input-argomento"
                disabled
                value="Primo colloquio · gratuito"
                className="input-editorial bg-ivory/60 cursor-not-allowed"
              />
            </Field>
          </div>

          <Field label="Messaggio" required className="mt-6">
            <textarea
              data-testid="contact-input-messaggio"
              value={form.messaggio}
              onChange={update("messaggio")}
              required
              minLength={10}
              rows={5}
              placeholder="Raccontami brevemente la tua situazione e cosa stai cercando..."
              className="input-editorial resize-none"
            />
          </Field>

          {/* Honeypot anti-bot: invisibile agli umani, i bot lo compilano */}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={update("website")}
            aria-hidden="true"
            style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", opacity: 0 }}
          />

          <label className="mt-8 flex items-start gap-3 text-sm text-muteText">
            <input
              data-testid="contact-input-privacy"
              type="checkbox"
              checked={form.consenso_privacy}
              onChange={update("consenso_privacy")}
              className="mt-1 accent-navy"
            />
            <span>
              Ho letto e accetto l'informativa privacy. Acconsento al trattamento dei
              miei dati personali al solo fine di ricevere risposta alla mia richiesta.
            </span>
          </label>

          <button
            data-testid="contact-submit-button"
            type="submit"
            disabled={loading}
            className="btn-primary mt-10 w-full md:w-auto justify-center disabled:opacity-50"
          >
            {loading ? "Invio in corso..." : "Invia richiesta"}
            {!loading && <ArrowRight size={16} />}
          </button>
        </form>
      </div>

      <style>{`
        .input-editorial {
          width: 100%;
          background: transparent;
          border: 0;
          border-bottom: 1px solid #C9C2B2;
          padding: 14px 2px;
          font-family: 'Manrope', sans-serif;
          font-size: 15px;
          color: #0A1128;
          outline: none;
          transition: border-color .3s ease;
        }
        .input-editorial:focus { border-bottom-color: #0A1128; }
        .input-editorial::placeholder { color: #8a8675; }
        textarea.input-editorial { border: 1px solid #E5E1D8; padding: 14px 16px; }
        textarea.input-editorial:focus { border-color: #0A1128; }
      `}</style>
    </section>
  );
};

const Field = ({ label, required, className = "", children }) => (
  <label className={`block ${className}`}>
    <span className="text-[11px] tracking-[0.22em] uppercase text-muteText">
      {label}
      {required && <span className="text-gold ml-1">*</span>}
    </span>
    <div className="mt-2">{children}</div>
  </label>
);
